import { updateTransactionNotification } from './api';

/**
 * Unread counts on a transaction, kept per party in its metadata (notifications.customer,
 * notifications.provider): the other party's goes up when someone acts on the order, and a
 * party's own goes back to zero when they open it. Called from TransactionPage.duck.
 */

const idOf = entity => entity?.id?.uuid || entity?.id;

/** The current user's role on the transaction ('customer' or 'provider'), or null. */
const roleOf = (transaction, currentUserId) => {
  if (!transaction || !currentUserId) {
    return null;
  }
  const currentUserUuid = currentUserId.uuid || currentUserId;
  if (idOf(transaction.customer) === currentUserUuid) {
    return 'customer';
  }
  if (idOf(transaction.provider) === currentUserUuid) {
    return 'provider';
  }
  return null;
};

/**
 * Counts one more unread action for the other party of the transaction.
 *
 * @param {Object} params
 * @param {Object} params.transaction - The transaction object
 * @param {UUID} params.currentUserId - The current user's ID (the one acting)
 * @returns {Promise} Resolves with the updated transaction data
 */
export const trackNotificationAuto = ({ transaction, currentUserId }) => {
  const role = roleOf(transaction, currentUserId);
  if (!role) {
    // Silently skip — TradMarket API event listener handles notifications
    return Promise.resolve();
  }
  const actor = role === 'customer' ? 'provider' : 'customer';
  return updateTransactionNotification({ transactionId: idOf(transaction), actor });
};

/**
 * Clears the current user's unread count on the transaction, as they open it.
 *
 * @param {Object} params
 * @param {Object} params.transaction - The transaction object
 * @param {UUID} params.currentUserId - The current user's ID
 * @returns {Promise} Resolves with the updated transaction data
 */
export const resetNotificationAuto = ({ transaction, currentUserId }) => {
  if (!transaction || !currentUserId) {
    console.error('resetNotificationAuto: transaction and currentUserId are required');
    return Promise.reject(new Error('transaction and currentUserId are required'));
  }
  const actor = roleOf(transaction, currentUserId);
  if (!actor) {
    console.error('Unable to determine actor to reset');
    return Promise.reject(new Error('Unable to determine actor to reset'));
  }
  return updateTransactionNotification({
    transactionId: idOf(transaction),
    actor,
    action: 'reset',
  });
};
